import { FC, useEffect, useState } from 'react';
import { noop } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import {
  RadioField as DSRadioField,
  FieldChangeEvent,
  NakedButton,
  PopoverDialog,
  SearchField,
  Text,
  useControlledField,
  usePopoverDialog,
} from '@frontend/design-system';
import {
  getTriggerButtonStyle,
  labelTextStyle,
  noResultsContainerStyle,
  popoverContentStyle,
  radioListStyle,
  searchFieldContainerStyle,
} from './radio-field.styles';

interface DropdownRadioFieldProps {
  options: Array<{ label: string; value: string }>;
  fieldId: string;
  value: string;
  onChange: (e?: FieldChangeEvent) => void;
  onBlur: () => void;
  hasError: boolean;
}

export const DropdownRadioField: FC<DropdownRadioFieldProps> = ({
  options,
  fieldId,
  value,
  onChange,
  onBlur,
  hasError,
}) => {
  const { t } = useTranslation('digital-forms-experience');
  const [searchValue, setSearchValue] = useState('');
  const [optionsToShow, setOptionsToShow] = useState<Array<{ label: string; value: string }>>([]);
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({
    placement: 'bottom',

    middlewareOptions: {
      size: {
        apply({ elements, rects }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
        padding: 8,
      },
    },
  });

  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchValue,
    onChange: (val: string) => setSearchValue(val),
  });

  // Filter options based on search value
  useEffect(() => {
    if (searchValue) {
      const filteredOptions = options?.filter(({ label }) => {
        if (!label) return false;
        return label.toLowerCase().includes(searchValue.toLowerCase());
      });
      setOptionsToShow(filteredOptions);
    } else {
      setOptionsToShow(options);
    }
  }, [searchValue, options]);

  const getLabel = () => {
    if (!value) {
      return <Text css={labelTextStyle}>{t('Select')}</Text>;
    }

    const selectedOption = options.find((opt) => opt.value === value);
    return <Text css={labelTextStyle}>{selectedOption?.label || value}</Text>;
  };

  return (
    <>
      <NakedButton css={getTriggerButtonStyle(hasError)} {...getTriggerProps()}>
        {getLabel()}
      </NakedButton>

      <PopoverDialog {...getDialogProps()} css={popoverContentStyle}>
        <div css={searchFieldContainerStyle}>
          <SearchField {...searchFieldProps} name={`${fieldId}-search`} placeholder={t('Search...')} />
        </div>

        {optionsToShow.length > 0 ? (
          <DSRadioField
            id={fieldId}
            name={fieldId}
            label=''
            value={value}
            error=''
            touched={false}
            active={false}
            aria-invalid={hasError}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={noop}
            css={radioListStyle}
          >
            {optionsToShow.map((option) => (
              <DSRadioField.Radio key={option.value} value={option.value}>
                {option.label}
              </DSRadioField.Radio>
            ))}
          </DSRadioField>
        ) : (
          <div css={noResultsContainerStyle}>
            <Text>{t('No matching results found!')}</Text>
          </div>
        )}
      </PopoverDialog>
    </>
  );
};
